import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { isSessionAuthError, SESSION_AUTH_MESSAGE } from '../utils/apiAuth';
import axios from 'axios';
import { fmtPrice, fmtDollar, fmtSpread } from '../utils/fmtPrice';
import {
  getHlSpreadSteps,
  getHlSpreadUnit,
  spreadStepLabel,
  stepsEqual,
  spreadStepStorageKey,
  loadSavedSpreadStep,
  saveSpreadStep,
  aggregateBookLevels,
} from '../utils/hlSpreadSteps';
import './LiveOrderBook.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

/** @deprecated Use getHlSpreadSteps(mid) — kept for any external imports. */
export const SPREAD_STEP_OPTIONS = getHlSpreadSteps(null);

function fmtTradeTime(ms) {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function deriveBookView(book, spreadStep) {
  if (!book) return null;
  if (!spreadStep || spreadStep <= 0) {
    return {
      ...book,
      spread:
        book.bestBid != null && book.bestAsk != null ? book.bestAsk - book.bestBid : book.spread,
    };
  }
  const bids = aggregateBookLevels(book.bids, spreadStep, 'bid');
  const asks = aggregateBookLevels(book.asks, spreadStep, 'ask');
  const bestBid = bids[0]?.price ?? null;
  const bestAsk = asks[0]?.price ?? null;
  const spread = bestBid != null && bestAsk != null ? bestAsk - bestBid : null;
  const mid = bestBid != null && bestAsk != null ? (bestBid + bestAsk) / 2 : book.mid;
  return {
    ...book,
    bids,
    asks,
    bestBid,
    bestAsk,
    spread,
    mid,
    spreadStep,
    long: book.long
      ? {
          ...book.long,
          entryPrice: bestAsk ?? book.long.entryPrice,
          exitPrice: bestBid ?? book.long.exitPrice,
        }
      : book.long,
    short: book.short
      ? {
          ...book.short,
          entryPrice: bestBid ?? book.short.entryPrice,
          exitPrice: bestAsk ?? book.short.exitPrice,
        }
      : book.short,
  };
}

export default function LiveOrderBook({
  symbol,
  hlDex = 'xyz',
  hlCoin = null,
  titlePrefix = 'Live book',
  apiPrefix = 'multi-asset',
  compact = false,
  pollMs = 30000,
  dollarPrices = false,
  spreadStepAdjustable = false,
  maxLevels = 8,
  showTradesTab = true,
}) {
  const fmt = dollarPrices ? fmtDollar : fmtPrice;
  const [activeTab, setActiveTab] = useState('book');
  const [book, setBook] = useState(null);
  const [bookError, setBookError] = useState(null);
  const [trades, setTrades] = useState([]);
  const [tradesError, setTradesError] = useState(null);
  const [spreadStep, setSpreadStep] = useState(() => getHlSpreadSteps(null)[0]);

  const marketParams = useCallback(() => {
    const params = {};
    if (hlDex && hlDex !== 'main') params.dex = hlDex;
    if (hlCoin) params.coin = hlCoin;
    return params;
  }, [hlDex, hlCoin]);

  const loadBook = useCallback(async () => {
    if (!symbol) return;
    try {
      const { data } = await axios.get(`${API_BASE}/${apiPrefix}/orderbook/${symbol}`, {
        headers: authHeaders(),
        params: marketParams(),
      });
      setBook(data);
      setBookError(null);
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.error || e.message;
      if (status === 429) {
        setBookError(`${msg} — refresh paused briefly (HL rate limit).`);
      } else if (isSessionAuthError(e)) {
        setBook(null);
        setBookError(SESSION_AUTH_MESSAGE);
      } else {
        setBook(null);
        setBookError(msg);
      }
    }
  }, [symbol, apiPrefix, marketParams]);

  const loadTrades = useCallback(async () => {
    if (!symbol || !showTradesTab) return;
    try {
      const { data } = await axios.get(`${API_BASE}/${apiPrefix}/recent-trades/${symbol}`, {
        headers: authHeaders(),
        params: marketParams(),
      });
      setTrades(data.trades || []);
      setTradesError(null);
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.error || e.message;
      if (status === 429) {
        setTradesError(`${msg} — refresh paused briefly (HL rate limit).`);
      } else {
        setTrades([]);
        setTradesError(msg);
      }
    }
  }, [symbol, apiPrefix, marketParams, showTradesTab]);

  useEffect(() => {
    if (activeTab === 'book') {
      loadBook();
    } else {
      loadTrades();
    }
    if (!pollMs) return undefined;
    const t = setInterval(() => {
      if (activeTab === 'book') loadBook();
      else loadTrades();
    }, pollMs);
    return () => clearInterval(t);
  }, [activeTab, loadBook, loadTrades, pollMs]);

  const midForSteps =
    book?.mid ??
    (book?.bestBid != null && book?.bestAsk != null
      ? (book.bestBid + book.bestAsk) / 2
      : null);
  const spreadUnit = getHlSpreadUnit(midForSteps);
  const spreadSteps = useMemo(() => getHlSpreadSteps(midForSteps), [spreadUnit]); // eslint-disable-line react-hooks/exhaustive-deps -- tier unit drives steps
  const storageKey = useMemo(
    () => spreadStepStorageKey(hlDex, hlCoin || symbol),
    [hlDex, hlCoin, symbol]
  );

  useEffect(() => {
    setActiveTab('book');
  }, [symbol, hlDex, hlCoin]);

  useEffect(() => {
    if (!spreadStepAdjustable) return;
    setSpreadStep(loadSavedSpreadStep(storageKey, getHlSpreadSteps(midForSteps)));
  }, [storageKey, spreadUnit, spreadStepAdjustable]); // midForSteps only via spreadUnit tier

  const selectSpreadStep = (step) => {
    setSpreadStep(step);
    saveSpreadStep(storageKey, step);
  };

  const refreshActive = () => {
    if (activeTab === 'book') loadBook();
    else loadTrades();
  };

  const view = deriveBookView(book, spreadStepAdjustable ? spreadStep : 0);
  const activeSpreadStep = spreadStepAdjustable ? spreadStep : 0;
  const displaySpread =
    view?.bestBid != null && view?.bestAsk != null ? view.bestAsk - view.bestBid : view?.spread;

  const headerSuffix =
    !compact && symbol ? (
      <>
        {' '}
        — {symbol}
        {hlDex && hlDex !== 'main' ? ` (${hlDex})` : ''}
      </>
    ) : null;

  return (
    <section className={`live-orderbook ${compact ? 'compact' : ''}`}>
      <div className="orderbook-header">
        {showTradesTab ? (
          <div className="orderbook-tabs" role="tablist" aria-label="Market data">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'book'}
              className={`orderbook-tab${activeTab === 'book' ? ' active' : ''}`}
              onClick={() => setActiveTab('book')}
            >
              Order book
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'trades'}
              className={`orderbook-tab${activeTab === 'trades' ? ' active' : ''}`}
              onClick={() => setActiveTab('trades')}
            >
              Trades
            </button>
          </div>
        ) : (
          <h2>
            {titlePrefix}
            {headerSuffix}
          </h2>
        )}
        <button type="button" className="secondary-btn" onClick={refreshActive}>
          Refresh
        </button>
      </div>

      {activeTab === 'book' && bookError && <p className="orderbook-error">{bookError}</p>}
      {activeTab === 'trades' && tradesError && <p className="orderbook-error">{tradesError}</p>}

      {activeTab === 'book' && view && (
        <>
          {spreadStepAdjustable && (
            <div className="orderbook-spread-step">
              <span className="orderbook-spread-step-label">Spread</span>
              <div className="orderbook-spread-step-options" role="group" aria-label="Spread step">
                {spreadSteps.map((step) => (
                  <button
                    key={step}
                    type="button"
                    className={`orderbook-spread-step-btn${stepsEqual(spreadStep, step) ? ' active' : ''}`}
                    onClick={() => selectSpreadStep(step)}
                  >
                    {spreadStepLabel(step)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="orderbook-summary">
            <div>
              <span className="label">Best bid</span>
              <strong>{fmt(view.bestBid)}</strong>
            </div>
            <div>
              <span className="label">Best ask</span>
              <strong>{fmt(view.bestAsk)}</strong>
            </div>
            <div>
              <span className="label">Spread</span>
              <strong>
                {displaySpread != null
                  ? fmtSpread(displaySpread, { spreadStep: activeSpreadStep, dollar: dollarPrices })
                  : '—'}
              </strong>
              {spreadStepAdjustable && activeSpreadStep > 0 && (
                <span className="orderbook-spread-note"> @ {spreadStepLabel(activeSpreadStep)} step</span>
              )}
            </div>
            <div>
              <span className="label">Mid</span>
              <strong>{fmt(view.mid)}</strong>
            </div>
          </div>

          {!compact && (
            <div className="orderbook-context">
              <div className="context-card long">
                <h3>Long reference</h3>
                <p>{view.long?.entryHint}</p>
                <p>
                  Entry ≈ <strong>{fmt(view.long?.entryPrice)}</strong> (ask)
                </p>
                <p>{view.long?.exitHint}</p>
                <p>
                  Exit ≈ <strong>{fmt(view.long?.exitPrice)}</strong> (bid)
                </p>
              </div>
              <div className="context-card short">
                <h3>Short reference</h3>
                <p>{view.short?.entryHint}</p>
                <p>
                  Entry ≈ <strong>{fmt(view.short?.entryPrice)}</strong> (bid)
                </p>
                <p>{view.short?.exitHint}</p>
                <p>
                  Exit ≈ <strong>{fmt(view.short?.exitPrice)}</strong> (ask)
                </p>
              </div>
            </div>
          )}

          <div className="orderbook-tables">
            <div>
              <h4>Bids (buyers)</h4>
              <table>
                <thead>
                  <tr>
                    <th>Price</th>
                    <th>Size</th>
                  </tr>
                </thead>
                <tbody>
                  {(view.bids || []).slice(0, maxLevels).map((l, i) => (
                    <tr key={`b${i}`}>
                      <td>{fmt(l.price)}</td>
                      <td>{l.size?.toFixed?.(4) ?? l.size}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <h4>Asks (sellers)</h4>
              <table>
                <thead>
                  <tr>
                    <th>Price</th>
                    <th>Size</th>
                  </tr>
                </thead>
                <tbody>
                  {(view.asks || []).slice(0, maxLevels).map((l, i) => (
                    <tr key={`a${i}`}>
                      <td>{fmt(l.price)}</td>
                      <td>{l.size?.toFixed?.(4) ?? l.size}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'trades' && showTradesTab && (
        <div className="orderbook-trades">
          {trades.length === 0 && !tradesError ? (
            <p className="orderbook-trades-empty">No recent trades yet.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Price</th>
                  <th>Size</th>
                  <th>Side</th>
                  <th>Date / time</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t) => (
                  <tr key={t.tid ?? `${t.time}-${t.price}-${t.size}`} className={`trade-row-${t.side}`}>
                    <td>{fmt(t.price)}</td>
                    <td>{Number.isFinite(t.size) ? t.size.toFixed(4) : t.size}</td>
                    <td>
                      <span className={`trade-side-pill ${t.side}`}>{t.sideLabel}</span>
                    </td>
                    <td>{fmtTradeTime(t.time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="orderbook-trades-foot">Recent fills from Hyperliquid (latest batch).</p>
        </div>
      )}
    </section>
  );
}
